import { Button, Tooltip } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { LuDownload } from 'react-icons/lu';
import { IoInformationCircleOutline } from 'react-icons/io5';

import { Table } from 'components/Table';
import { TextM } from 'components/Text';
import theme from 'theme/theme';
import {
	formatCurrency,
	formatToCurrencyFloat,
	normalizeCpfCnpj,
} from 'helpers/normalizers';

import { ImportRecordStatus, TransferImportRecord } from '../types';

export const TRANSFER_PAGE_TITLE = 'Visualizar transferência em lote';

// A API devolve `valor` como string no formato pt-BR ("1.234,56"), apesar do tipo.
const parseValor = (value: unknown): number | null => {
	if (typeof value === 'number') {
		return Number.isFinite(value) ? value : null;
	}

	if (typeof value !== 'string' || !value.trim()) {
		return null;
	}

	const parsed = Number(formatToCurrencyFloat(value.trim()));
	return Number.isFinite(parsed) ? parsed : null;
};

const recordStatusLabels: Record<ImportRecordStatus, string> = {
	[ImportRecordStatus.SUCCESS]: 'Sucesso',
	[ImportRecordStatus.ERROR]: 'Erro',
};

interface TransferImportTableProps {
	records: TransferImportRecord[];
	isLoading: boolean;
}

export const TransferImportTable = ({
	records,
	isLoading,
}: TransferImportTableProps) => {
	const columns: ColumnsType<TransferImportRecord> = [
		{
			title: 'Documento',
			key: 'documento',
			render: (record: TransferImportRecord) => (
				<TextM weight="bold">
					{record.data.documento
						? normalizeCpfCnpj(record.data.documento)
						: '-'}
				</TextM>
			),
			width: '10%',
		},
		{
			title: 'Nome do beneficiário',
			key: 'nome_do_beneficiario',
			render: (record: TransferImportRecord) => (
				<>
					{record.data.nome_do_beneficiario ? (
						<Tooltip title={record.data.nome_do_beneficiario}>
							<TextM
								weight="bold"
								style={{
									whiteSpace: 'nowrap',
									overflow: 'hidden',
									textOverflow: 'ellipsis',
									maxWidth: '150px',
								}}
							>
								{record.data.nome_do_beneficiario}
							</TextM>
						</Tooltip>
					) : (
						<TextM weight="bold">-</TextM>
					)}
				</>
			),
			width: '10%',
		},
		{
			title: 'Tipo de Movimentação',
			key: 'tipo_de_movimentacao',
			render: (record: TransferImportRecord) => (
				<TextM weight="bold">
					{record.data.tipo_de_movimentacao ?? '-'}
				</TextM>
			),
			width: '10%',
		},
		{
			title: 'Tipo de chave',
			key: 'tipo_de_chave',
			render: (record: TransferImportRecord) => (
				<TextM weight="normal">
					{record.data.tipo_de_chave ?? '-'}
				</TextM>
			),
			width: '6%',
		},
		{
			title: 'Chave Pix',
			key: 'chave_pix',
			render: (record: TransferImportRecord) => (
				<TextM weight="bold">{record.data.chave_pix ?? '-'}</TextM>
			),
			width: '10%',
		},
		{
			title: 'Tipo de Conta',
			key: 'tipo_de_conta',
			render: (record: TransferImportRecord) => (
				<TextM weight="normal">
					{record.data.tipo_de_conta ?? '-'}
				</TextM>
			),
			width: '8%',
		},
		{
			title: 'ISPB',
			key: 'ispb',
			render: (record: TransferImportRecord) => (
				<>
					{record.data.ispb ? (
						<Tooltip title={record.data.ispb}>
							<TextM
								weight="normal"
								style={{
									whiteSpace: 'nowrap',
									overflow: 'hidden',
									textOverflow: 'ellipsis',
									maxWidth: '150px',
								}}
							>
								{record.data.ispb}
							</TextM>
						</Tooltip>
					) : (
						<TextM weight="normal">-</TextM>
					)}
				</>
			),
			width: '6%',
		},
		{
			title: 'Agência',
			key: 'agencia',
			render: (record: TransferImportRecord) => (
				<TextM weight="normal">
					{record.data.agencia
						? String(record.data.agencia).padStart(4, '0')
						: '-'}
				</TextM>
			),
			width: '6%',
		},
		{
			title: 'Conta',
			key: 'conta',
			render: (record: TransferImportRecord) => (
				<TextM weight="normal">{record.data.conta ?? '-'}</TextM>
			),
			width: '8%',
		},
		{
			title: 'Valor',
			key: 'valor',
			render: (record: TransferImportRecord) => (
				<TextM weight="bold">
					{formatCurrency(parseValor(record.data.valor))}
				</TextM>
			),
			width: '8%',
		},
		{
			title: 'Data Agendamento',
			key: 'data_agendamento',
			render: (record: TransferImportRecord) => (
				<TextM weight="bold">
					{record.data.data_agendamento ?? '-'}
				</TextM>
			),
			width: '10%',
		},
		{
			title: 'Descrição',
			key: 'descricao',
			render: (record: TransferImportRecord) => (
				<>
					{record.data.descricao ? (
						<Tooltip title={record.data.descricao}>
							<TextM
								weight="normal"
								style={{
									whiteSpace: 'nowrap',
									overflow: 'hidden',
									textOverflow: 'ellipsis',
									maxWidth: '150px',
								}}
							>
								{record.data.descricao}
							</TextM>
						</Tooltip>
					) : (
						<TextM weight="normal">-</TextM>
					)}
				</>
			),
			width: '10%',
		},
		{
			title: 'Status',
			key: 'status',
			render: (record: TransferImportRecord) => (
				<TextM weight="normal">
					{recordStatusLabels[record.status]}
				</TextM>
			),
			width: '8%',
		},
	];

	return (
		<Table
			rowKey={(_, index) => String(index)}
			columns={columns}
			dataSource={records}
			loading={isLoading}
			scroll={{ x: 1000, y: 420 }}
			pagination={false}
		/>
	);
};

interface TransferDownloadActionsProps {
	onDownload: () => void;
}

export const TransferDownloadActions = ({
	onDownload,
}: TransferDownloadActionsProps) => (
	<Button
		type="primary"
		onClick={onDownload}
		icon={<LuDownload size={16} />}
		iconPosition="end"
	>
		Baixar arquivo
	</Button>
);

interface TransferFooterLeftProps {
	records: TransferImportRecord[];
}

export const TransferFooterLeft = ({ records }: TransferFooterLeftProps) => {
	const totalAmount = records.reduce((sum, record) => {
		if (record.status !== ImportRecordStatus.SUCCESS) return sum;

		const valor = parseValor(record.data.valor);
		return valor !== null ? sum + valor : sum;
	}, 0);

	const errorCount = records.filter(
		record => record.status === ImportRecordStatus.ERROR,
	).length;

	const errorTooltip =
		errorCount === 1
			? '1 linha com erro não foi incluída no total.'
			: `${errorCount} linhas com erro não foram incluídas no total.`;

	return (
		<div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
			<TextM weight="bold">{`Valor total: ${formatCurrency(
				totalAmount,
			)}`}</TextM>
			{errorCount > 0 && (
				<Tooltip title={errorTooltip}>
					<span style={{ display: 'inline-flex' }}>
						<IoInformationCircleOutline
							size={'1.1rem'}
							color={theme.textGray}
						/>
					</span>
				</Tooltip>
			)}
		</div>
	);
};
